import { hideOthers } from 'aria-hidden';
import classNames from 'classnames';
import React, { forwardRef, KeyboardEvent, ReactNode, useEffect, useRef } from 'react';
import { ESC_KEY } from '../utils/keyboard';
import { shareRefs } from '../utils/react';
import './dialog.sass';
import FocusTrap from './focus-trap';

interface Props {
  alert?: boolean;
  ariaDescribedBy?: string;
  ariaHiddenScope?: HTMLElement | null;
  ariaLabelledBy: string;
  children?: ReactNode;
  className?: string;
  dataTestId?: string;
  onDismiss: () => void;
  scrim?: boolean;
  preventRefocus?: boolean;
}

const Dialog = forwardRef<HTMLDivElement, Props>(
  (
    {
      alert,
      ariaDescribedBy,
      ariaHiddenScope,
      ariaLabelledBy,
      children,
      className,
      dataTestId,
      onDismiss,
      scrim,
      preventRefocus,
    }: Props,
    ref,
  ) => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === ESC_KEY) {
        e.preventDefault();
        onDismiss();
      }
    }

    const self = useRef<HTMLDivElement>(null);
    const setRef = shareRefs(self, ref);
    const prev = useRef(document.activeElement);
    const { scrollLeft = 0, scrollTop = 0 } = document.scrollingElement || {};
    const scroll = useRef({ scrollLeft, scrollTop });

    useEffect(() => {
      function onClickOutside(e: MouseEvent) {
        if (e.target instanceof Node && !self.current?.contains(e.target)) {
          onDismiss();
        }
      }

      function cleanup() {
        // KU – restore previously focused element
        if (prev.current instanceof HTMLElement && !preventRefocus) {
          prev.current.focus();
        }

        // KU – restore viewport position
        requestAnimationFrame(() => {
          const { scrollLeft: x, scrollTop: y } = scroll.current;
          document.scrollingElement?.scrollTo?.(x, y);
        });
      }

      if (self.current) {
        window.addEventListener('click', onClickOutside, true);
        const undo = hideOthers(self.current, ariaHiddenScope || undefined);
        setTimeout(() => {
          if (!self.current?.contains(document.activeElement)) {
            self.current?.focus();
          }
        }, 0);
        return () => {
          cleanup();
          window.removeEventListener('click', onClickOutside, true);
          undo();
        };
      }
      return cleanup;
    }, []);

    const containerClass = classNames('dialog-container', { scrim });
    const dialogClass = classNames('dialog-content', className);
    return (
      <div className={containerClass}>
        <FocusTrap>
          <div
            onKeyDown={onKeyDown}
            aria-labelledby={ariaLabelledBy}
            aria-describedby={ariaDescribedBy}
            className={dialogClass}
            data-testid={dataTestId}
            ref={setRef}
            role={alert ? 'alertdialog' : 'dialog'}
            tabIndex={-1}
          >
            {children}
          </div>
        </FocusTrap>
      </div>
    );
  },
);

export default Dialog;

Dialog.displayName = 'Dialog';
